import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Workspace, CreateWorkspaceRequest, UpdateWorkspaceRequest } from '@/types';
import { workspacesService } from '@/services/workspaces';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

interface WorkspaceContextType {
  currentWorkspace: Workspace | null;
  workspaces: Workspace[];
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isSwitching: boolean;
  
  // Workspace operations
  loadWorkspaces: () => Promise<void>;
  createWorkspace: (data: CreateWorkspaceRequest) => Promise<Workspace>;
  updateWorkspace: (id: string, data: UpdateWorkspaceRequest) => Promise<Workspace>;
  deleteWorkspace: (id: string) => Promise<void>;
  switchWorkspace: (id: string) => Promise<void>;
  duplicateWorkspace: (id: string) => Promise<Workspace>;
  
  // Workspace selection
  setCurrentWorkspace: (workspace: Workspace | null) => void;
  getCurrentWorkspaceId: () => string | null;
  
  // Workspace initialization
  initializeWorkspaceContext: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

interface WorkspaceProviderProps {
  children: ReactNode;
}

export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  // Initialize workspace context when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      initializeWorkspaceContext();
    } else {
      // Clear workspace context when user is not authenticated
      setCurrentWorkspace(null);
      setWorkspaces([]);
      workspacesService.clearCurrentWorkspaceId();
    }
  }, [isAuthenticated, user]);

  const loadWorkspaces = async () => {
    if (!isAuthenticated) return;
    
    try {
      setIsLoading(true);
      console.log('Loading workspaces...');
      
      const response = await workspacesService.getWorkspaces({ page: 1, per_page: 100 });
      setWorkspaces(response.items);
      
      console.log(`Loaded ${response.items.length} workspaces`);
    } catch (error: any) {
      console.error('Error loading workspaces:', error);
      toast.error(error.message || 'Failed to load workspaces');
    } finally {
      setIsLoading(false);
    }
  };

  const createWorkspace = async (data: CreateWorkspaceRequest): Promise<Workspace> => {
    try {
      setIsCreating(true);
      console.log('Creating workspace:', data);
      
      const newWorkspace = await workspacesService.createWorkspace(data);
      
      // Add to workspaces list
      setWorkspaces(prev => [newWorkspace, ...prev]);
      
      // Set as current workspace
      setCurrentWorkspace(newWorkspace);
      workspacesService.setCurrentWorkspaceId(newWorkspace.id);
      
      // Invalidate all queries to refresh UI
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      
      toast.success(`Workspace "${newWorkspace.name}" created successfully!`);
      console.log('Workspace created successfully:', newWorkspace);
      
      return newWorkspace;
    } catch (error: any) {
      console.error('Error creating workspace:', error);
      toast.error(error.message || 'Failed to create workspace');
      throw error;
    } finally {
      setIsCreating(false);
    }
  };

  const updateWorkspace = async (id: string, data: UpdateWorkspaceRequest): Promise<Workspace> => {
    try {
      setIsUpdating(true);
      console.log(`Updating workspace ${id}:`, data);
      
      const updatedWorkspace = await workspacesService.updateWorkspace(id, data);
      
      // Update in workspaces list
      setWorkspaces(prev => prev.map(w => w.id === id ? updatedWorkspace : w));
      
      // Update current workspace if it's the one being updated
      if (currentWorkspace?.id === id) {
        setCurrentWorkspace(updatedWorkspace);
      }
      
      toast.success(`Workspace "${updatedWorkspace.name}" updated successfully!`);
      console.log('Workspace updated successfully:', updatedWorkspace);
      
      return updatedWorkspace;
    } catch (error: any) {
      console.error('Error updating workspace:', error);
      toast.error(error.message || 'Failed to update workspace');
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteWorkspace = async (id: string): Promise<void> => {
    try {
      setIsDeleting(true);
      console.log(`Deleting workspace ${id}...`);
      
      const workspaceToDelete = workspaces.find(w => w.id === id);
      
      await workspacesService.deleteWorkspace(id);
      
      // Remove from workspaces list
      setWorkspaces(prev => prev.filter(w => w.id !== id));
      
      // If current workspace was deleted, switch to another one
      if (currentWorkspace?.id === id) {
        const remainingWorkspaces = workspaces.filter(w => w.id !== id);
        if (remainingWorkspaces.length > 0) {
          await switchWorkspace(remainingWorkspaces[0].id);
        } else {
          setCurrentWorkspace(null);
          workspacesService.clearCurrentWorkspaceId();
        }
      }
      
      toast.success(`Workspace "${workspaceToDelete?.name}" deleted successfully!`);
      console.log('Workspace deleted successfully');
    } catch (error: any) {
      console.error('Error deleting workspace:', error);
      toast.error(error.message || 'Failed to delete workspace');
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  const switchWorkspace = async (id: string): Promise<void> => {
    try {
      setIsSwitching(true);
      console.log(`Switching to workspace ${id}...`);
      
      const result = await workspacesService.switchWorkspace(id);
      
      // Find the workspace in our list
      const workspace = workspaces.find(w => w.id === id);
      if (workspace) {
        setCurrentWorkspace(workspace);
        
        // Invalidate all workspace-specific queries
        queryClient.invalidateQueries({ queryKey: ['agents'] });
        queryClient.invalidateQueries({ queryKey: ['workflows'] });
        queryClient.invalidateQueries({ queryKey: ['integrations'] });
        
        toast.success(result.message);
        console.log('Workspace switched successfully:', workspace);
      } else {
        // If workspace not in list, fetch it
        const fetchedWorkspace = await workspacesService.getWorkspace(id);
        setCurrentWorkspace(fetchedWorkspace);
        
        // Invalidate all workspace-specific queries
        queryClient.invalidateQueries({ queryKey: ['agents'] });
        queryClient.invalidateQueries({ queryKey: ['workflows'] });
        queryClient.invalidateQueries({ queryKey: ['integrations'] });
        
        toast.success(`Switched to workspace "${fetchedWorkspace.name}"`);
      }
    } catch (error: any) {
      console.error('Error switching workspace:', error);
      toast.error(error.message || 'Failed to switch workspace');
      throw error;
    } finally {
      setIsSwitching(false);
    }
  };

  const duplicateWorkspace = async (id: string): Promise<Workspace> => {
    try {
      console.log(`Duplicating workspace ${id}...`);
      
      const duplicatedWorkspace = await workspacesService.duplicateWorkspace(id);
      
      // Add to workspaces list
      setWorkspaces(prev => [duplicatedWorkspace, ...prev]);
      
      toast.success(`Workspace "${duplicatedWorkspace.name}" duplicated successfully!`);
      console.log('Workspace duplicated successfully:', duplicatedWorkspace);
      
      return duplicatedWorkspace;
    } catch (error: any) {
      console.error('Error duplicating workspace:', error);
      toast.error(error.message || 'Failed to duplicate workspace');
      throw error;
    }
  };

  const getCurrentWorkspaceId = (): string | null => {
    return workspacesService.getCurrentWorkspaceId();
  };

  const initializeWorkspaceContext = async (): Promise<void> => {
    if (!isAuthenticated) return;
    
    try {
      console.log('Initializing workspace context...');
      
      // Load all workspaces
      await loadWorkspaces();
      
      // Try to get current workspace from localStorage
      const currentWorkspaceId = workspacesService.getCurrentWorkspaceId();
      
      if (currentWorkspaceId) {
        try {
          const workspace = await workspacesService.getWorkspace(currentWorkspaceId);
          setCurrentWorkspace(workspace);
          console.log('Current workspace restored:', workspace);
        } catch (error) {
          console.error('Failed to restore current workspace:', error);
          // Clear invalid workspace ID and try to set first available
          workspacesService.clearCurrentWorkspaceId();
        }
      }
      
      // If no current workspace, try to initialize with first available
      if (!currentWorkspace) {
        const initialWorkspace = await workspacesService.initializeWorkspaceContext();
        if (initialWorkspace) {
          setCurrentWorkspace(initialWorkspace);
          console.log('Workspace context initialized with:', initialWorkspace);
        }
      }
    } catch (error) {
      console.error('Error initializing workspace context:', error);
    }
  };

  const value: WorkspaceContextType = {
    currentWorkspace,
    workspaces,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isSwitching,
    
    loadWorkspaces,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    switchWorkspace,
    duplicateWorkspace,
    
    setCurrentWorkspace,
    getCurrentWorkspaceId,
    
    initializeWorkspaceContext,
  };

  console.log('WorkspaceContext state:', {
    currentWorkspace: currentWorkspace?.name,
    workspacesCount: workspaces.length,
    isLoading,
    isAuthenticated
  });

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}; 